import React, { useEffect, useMemo, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { Box, IconButton, Tooltip as MuiTooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { buildChartZoomPluginOptions } from '../utils/chartZoomOptions';
import { formatDateTimeDisplay } from '../utils/dateBucketFormat';
import {
  DAY_MS,
  HOUR_MS,
  MINUTE_MS,
  buildIntervalLayout,
  buildTimeTicks,
  formatIntervalDateTime,
  formatIntervalDuration,
  formatTimeTick,
  resolveIntervalColumns,
} from '../utils/intervalChart';

function formatIntervalInstant(ms, dateDisplayFormat) {
  if (dateDisplayFormat && dateDisplayFormat !== 'auto') {
    const formatted = formatDateTimeDisplay(new Date(ms), { dateDisplayFormat });
    if (formatted) return formatted;
  }
  return formatIntervalDateTime(ms);
}

const LANE_PX = 30;

/**
 * Gráfico de intervalos: barras horizontales de inicio a fin sobre un eje de tiempo
 * continuo, una fila por serie (carriles extra cuando hay solapamiento).
 */
export default function IntervalTimelineChart({
  data,
  intervalConfig,
  colorFor,
  zoomEnabled = true,
  legendDisplay = true,
}) {
  const theme = useTheme();
  const chartRef = useRef(null);
  const containerRef = useRef(null);

  const cfgStart = intervalConfig?.startColumn || '';
  const cfgEnd = intervalConfig?.endColumn || '';
  const cfgSeries = intervalConfig?.seriesColumn || '';
  const startDateFormat = intervalConfig?.startDateDisplayFormat || '';
  const endDateFormat = intervalConfig?.endDateDisplayFormat || '';
  const columns = useMemo(
    () => resolveIntervalColumns(data, { startColumn: cfgStart, endColumn: cfgEnd, seriesColumn: cfgSeries }),
    [data, cfgStart, cfgEnd, cfgSeries]
  );
  const layout = useMemo(
    () => buildIntervalLayout(data || [], columns),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, columns.startColumn, columns.endColumn, columns.seriesColumn]
  );
  const hasSeries = Boolean(columns.seriesColumn);

  const seriesColors = layout.series.map((serie, si) => colorFor(serie || 'Intervalos', si).border);
  const seriesColorsKey = seriesColors.join('|');

  const chartData = useMemo(() => {
    if (!layout.items.length) return null;
    const labels = layout.lanes.map((_, i) => String(i));
    const colors = seriesColorsKey.split('|');
    const datasets = layout.series.map((serie, si) => {
      const c = { border: colors[si] };
      return {
        label: serie || 'Intervalos',
        data: layout.items
          .filter((it) => it.serie === serie)
          .map((it) => ({ x: [it.start, it.end], y: String(it.laneIndex), item: it })),
        backgroundColor: c.border,
        borderColor: c.border,
        borderWidth: 0,
        borderRadius: 3,
        borderSkipped: false,
        grouped: false,
        barPercentage: 0.75,
        categoryPercentage: 0.95,
        minBarLength: 3,
      };
    });
    return { labels, datasets };
  }, [layout, seriesColorsKey]);

  const options = useMemo(() => {
    if (!chartData) return null;
    const span = Math.max(layout.max - layout.min, HOUR_MS);
    const pad = Math.max(15 * MINUTE_MS, span * 0.02);
    const xMin = layout.min - pad;
    const xMax = layout.max + pad;
    const gridColor = theme.palette.divider;
    const midnightColor = theme.palette.text.disabled;
    const zoom = zoomEnabled ? buildChartZoomPluginOptions({ enabled: true, type: 'bar' }) : undefined;
    if (zoom) zoom.limits = { x: { min: 'original', max: 'original', minRange: 15 * MINUTE_MS } };

    return {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      scales: {
        x: {
          type: 'linear',
          position: 'bottom',
          min: xMin,
          max: xMax,
          afterBuildTicks(scale) {
            const maxTicks = Math.max(3, Math.floor((scale.width || 600) / 90));
            const { ticks, step } = buildTimeTicks(scale.min, scale.max, maxTicks);
            scale.intervalTickStep = step;
            scale.ticks = ticks.map((value) => ({ value }));
          },
          ticks: {
            autoSkip: false,
            maxRotation: 0,
            callback(value, index) {
              if (startDateFormat) {
                const formatted = formatDateTimeDisplay(new Date(value), { dateDisplayFormat: startDateFormat });
                if (formatted) return formatted;
              }
              return formatTimeTick(value, this.intervalTickStep || HOUR_MS, index === 0);
            },
          },
          grid: {
            color: (ctx) =>
              ctx.tick && ctx.tick.value % DAY_MS === 0 ? midnightColor : gridColor,
          },
        },
        y: {
          type: 'category',
          offset: true,
          grid: { display: false },
          ticks: {
            autoSkip: false,
            callback(value, index) {
              const lane = layout.lanes[index];
              if (!lane || !hasSeries) return '';
              return lane.lane === 0 ? lane.serie : '';
            },
          },
        },
      },
      plugins: {
        legend: { display: legendDisplay && hasSeries && layout.series.length > 1, position: 'top' },
        tooltip: {
          callbacks: {
            title: (items) => items?.[0]?.raw?.item?.serie || '',
            label: (ctx) => {
              const it = ctx.raw?.item;
              if (!it) return '';
              return [
                `Inicio: ${formatIntervalInstant(it.start, startDateFormat)}`,
                `Fin: ${formatIntervalInstant(it.end, endDateFormat || startDateFormat)}`,
                `Duración: ${formatIntervalDuration(it.start, it.end)}`,
              ];
            },
          },
        },
        ...(zoom ? { zoom } : {}),
      },
    };
  }, [chartData, layout, hasSeries, zoomEnabled, legendDisplay, theme, startDateFormat, endDateFormat]);

  useEffect(() => {
    if (!zoomEnabled) return undefined;
    const node = containerRef.current;
    if (!node) return undefined;
    let lastTapAt = 0;
    const onTouchEnd = (event) => {
      if (event.touches.length > 0) return;
      const now = Date.now();
      if (now - lastTapAt < 320) {
        chartRef.current?.resetZoom?.();
        lastTapAt = 0;
        event.preventDefault();
        return;
      }
      lastTapAt = now;
    };
    const onDblClick = () => chartRef.current?.resetZoom?.();
    node.addEventListener('touchend', onTouchEnd, { passive: false });
    node.addEventListener('dblclick', onDblClick);
    return () => {
      node.removeEventListener('touchend', onTouchEnd);
      node.removeEventListener('dblclick', onDblClick);
    };
  }, [zoomEnabled, chartData]);

  if (!columns.startColumn || !columns.endColumn) {
    return (
      <Box sx={{ height: '100%', minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Typography color="text.secondary" variant="body2" textAlign="center">
          Elige las columnas de inicio y fin del intervalo
        </Typography>
      </Box>
    );
  }

  if (!chartData) {
    return (
      <Box sx={{ height: '100%', minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">No hay intervalos para mostrar</Typography>
      </Box>
    );
  }

  const minHeight = Math.max(180, layout.lanes.length * LANE_PX + 90);

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        minHeight,
        position: 'relative',
        touchAction: zoomEnabled ? 'pan-y pinch-zoom' : undefined,
      }}
    >
      {zoomEnabled && (
        <MuiTooltip title="Restablecer zoom (doble clic)">
          <IconButton
            size="small"
            aria-label="Restablecer zoom"
            onClick={() => chartRef.current?.resetZoom?.()}
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              zIndex: 2,
              bgcolor: 'background.paper',
              boxShadow: 1,
              opacity: 0.88,
              '&:hover': { opacity: 1 },
            }}
          >
            <ZoomOutMapIcon fontSize="small" />
          </IconButton>
        </MuiTooltip>
      )}
      <Chart ref={chartRef} type="bar" data={chartData} options={options} />
    </Box>
  );
}
